import { mapPinToPassword } from './pinMapping';

const REGION = process.env.REACT_APP_COGNITO_REGION;
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;
const ENDPOINT = `https://cognito-idp.${REGION}.amazonaws.com/`;

export class AuthError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}

const FRIENDLY_MESSAGES = {
  NotAuthorizedException: "That phone/email or PIN doesn't match our records.",
  UserNotFoundException: "That phone/email or PIN doesn't match our records.",
  UsernameExistsException: 'An account with this phone number already exists.',
  UserNotConfirmedException: "This account hasn't been confirmed yet.",
  CodeMismatchException: "That code doesn't match. Please check and try again.",
  ExpiredCodeException: 'This code has expired. Request a new one.',
  LimitExceededException: 'Too many attempts. Please wait a bit and try again.',
  TooManyRequestsException: 'Too many attempts. Please wait a bit and try again.',
  TooManyFailedAttemptsException: 'Too many attempts. Please wait a bit and try again.',
  InvalidPasswordException: "That PIN doesn't meet the account's requirements.",
  InvalidParameterException: 'Something about that request was invalid. Please check and try again.',
  NetworkError: 'Could not reach the server. Check your connection and try again.',
};

function friendlyMessage(code) {
  return FRIENDLY_MESSAGES[code] || 'Something went wrong. Please try again.';
}

async function cognitoRequest(action, body) {
  let response;
  try {
    response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new AuthError('NetworkError', friendlyMessage('NetworkError'));
  }

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const code = data.__type ? data.__type.split('#').pop() : 'UnknownError';
    throw new AuthError(code, friendlyMessage(code));
  }

  return data;
}

export function phoneToUsername(phone) {
  return `p${phone.replace('+', '')}`;
}

export async function initiateAuthWithPin(usernameOrAlias, pin) {
  const data = await cognitoRequest('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: CLIENT_ID,
    AuthParameters: {
      USERNAME: usernameOrAlias,
      PASSWORD: mapPinToPassword(pin),
    },
  });
  return data.AuthenticationResult;
}

export async function refreshAuth(refreshToken) {
  // GetTokensFromRefreshToken works with or without refresh-token rotation.
  // InitiateAuth + REFRESH_TOKEN_AUTH fails when rotation is enabled on the app client.
  const data = await cognitoRequest('GetTokensFromRefreshToken', {
    ClientId: CLIENT_ID,
    RefreshToken: refreshToken,
  });
  return data.AuthenticationResult;
}

export async function signUp({ phone, email, pin }) {
  const username = phoneToUsername(phone);
  await cognitoRequest('SignUp', {
    ClientId: CLIENT_ID,
    Username: username,
    Password: mapPinToPassword(pin),
    UserAttributes: [
      { Name: 'email', Value: email },
      { Name: 'phone_number', Value: phone },
    ],
  });
  return { username };
}

export async function confirmSignUp(username, code) {
  await cognitoRequest('ConfirmSignUp', {
    ClientId: CLIENT_ID,
    Username: username,
    ConfirmationCode: code,
  });
}

export async function resendConfirmationCode(username) {
  await cognitoRequest('ResendConfirmationCode', {
    ClientId: CLIENT_ID,
    Username: username,
  });
}

export async function forgotPassword(email) {
  await cognitoRequest('ForgotPassword', {
    ClientId: CLIENT_ID,
    Username: email,
  });
}

export async function confirmForgotPassword(email, code, newPin) {
  await cognitoRequest('ConfirmForgotPassword', {
    ClientId: CLIENT_ID,
    Username: email,
    ConfirmationCode: code,
    Password: mapPinToPassword(newPin),
  });
}

export async function globalSignOut(accessToken) {
  try {
    await cognitoRequest('GlobalSignOut', { AccessToken: accessToken });
  } catch {
    // best-effort — local session is cleared regardless of server-side outcome
  }
}
